/** Select complete OCR boxes in page order; glyph metrics never trim the value. */
export function selectOcrBoxes(viewer: HTMLElement, first: SVGTextElement, last: SVGTextElement) {
  if (first.closest('svg') !== last.closest('svg')) return;
  const words = [
    ...first.closest('svg')!.querySelectorAll<SVGTextElement>('text[data-line-index]'),
  ];
  const from = Math.min(words.indexOf(first), words.indexOf(last));
  const to = Math.max(words.indexOf(first), words.indexOf(last));
  const start = words[from]?.firstChild;
  const end = words[to]?.firstChild;
  if (!start || !end) return;
  const range = window.document.createRange();
  range.setStart(start, 0);
  range.setEnd(end, end.textContent?.length ?? 0);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
  viewer
    .querySelectorAll('.ocr-box-selected')
    .forEach((word) => word.classList.remove('ocr-box-selected'));
  words
    .slice(from, to + 1)
    .forEach((word) => word.parentElement?.classList.add('ocr-box-selected'));
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function bindOcrBoxSelection(viewer: HTMLElement): () => void {
  let anchor: SVGTextElement | null = null;
  let start = { x: 0, y: 0 };
  const wordAt = (x: number, y: number) =>
    [...viewer.querySelectorAll<SVGTextElement>('text[data-line-index]')].find((word) => {
      const box = word.parentElement?.querySelector('rect')?.getBoundingClientRect();
      return box && x >= box.left && x <= box.right && y >= box.top && y <= box.bottom;
    }) ?? null;
  const down = (event: PointerEvent) => {
    start = { x: event.clientX, y: event.clientY };
    anchor = event.button === 0 ? wordAt(event.clientX, event.clientY) : null;
    if (event.button !== 0) return;
    viewer
      .querySelectorAll('.ocr-box-selected')
      .forEach((word) => word.classList.remove('ocr-box-selected'));
    viewer.dataset.ocrSelecting = anchor ? 'true' : 'false';
    if (anchor) {
      event.preventDefault();
      selectOcrBoxes(viewer, anchor, anchor);
    }
  };
  const move = (event: PointerEvent) => {
    if (!anchor || !(event.buttons & 1)) return;
    event.preventDefault();
    const words = [
      ...anchor.closest('svg')!.querySelectorAll<SVGTextElement>('text[data-line-index]'),
    ];
    const entries = words.map((word, index) => ({
      word,
      index,
      box: word.parentElement!.querySelector('rect')!.getBoundingClientRect(),
    }));
    const anchorIndex = words.indexOf(anchor);
    // Keep tracking in gaps and beyond the last box, so fast pointer moves
    // cannot skip a word. Selection crosses each word at its horizontal midpoint.
    const closest = entries.reduce((best, entry) =>
      Math.abs(event.clientY - (entry.box.top + entry.box.height / 2)) <
      Math.abs(event.clientY - (best.box.top + best.box.height / 2))
        ? entry
        : best,
    );
    const row = entries.filter(
      (entry) => entry.word.dataset.lineIndex === closest.word.dataset.lineIndex,
    );
    const backward =
      closest.index < anchorIndex ||
      (closest.word.dataset.lineIndex === anchor.dataset.lineIndex && event.clientX < start.x);
    const crossed = row.filter((entry) =>
      backward
        ? event.clientX <= entry.box.left + entry.box.width / 2 + 1
        : event.clientX >= entry.box.left + entry.box.width / 2 - 1,
    );
    const end = backward ? crossed[0] : crossed.at(-1);
    if (end) selectOcrBoxes(viewer, anchor, end.word);
    else if (closest.word.dataset.lineIndex === anchor.dataset.lineIndex)
      selectOcrBoxes(viewer, anchor, anchor);
  };
  const click = (event: MouseEvent) => {
    if (
      event.button !== 0 ||
      !anchor ||
      Math.hypot(event.clientX - start.x, event.clientY - start.y) > 4
    )
      return;
    const words =
      event.detail >= 2
        ? [
            ...anchor.closest('svg')!.querySelectorAll<SVGTextElement>('text[data-line-index]'),
          ].filter((word) => word.dataset.lineIndex === anchor!.dataset.lineIndex)
        : [anchor];
    if (words.length) selectOcrBoxes(viewer, words[0], words[words.length - 1]);
  };
  const up = () => {
    delete viewer.dataset.ocrSelecting;
  };
  viewer.addEventListener('pointerup', up);
  viewer.addEventListener('pointercancel', up);
  viewer.addEventListener('click', click);
  viewer.addEventListener('pointerdown', down);
  viewer.addEventListener('pointermove', move);
  return () => {
    up();
    viewer.removeEventListener('pointerup', up);
    viewer.removeEventListener('pointercancel', up);
    viewer.removeEventListener('click', click);
    viewer.removeEventListener('pointerdown', down);
    viewer.removeEventListener('pointermove', move);
    viewer
      .querySelectorAll('.ocr-box-selected')
      .forEach((word) => word.classList.remove('ocr-box-selected'));
  };
}

/** Join words on a printed row with spaces; retain actual row breaks. */
export function ocrBoxText(words: SVGTextElement[]): string {
  return words.reduce((text, word, index) => {
    const value = word.textContent?.replace(/\s+/g, ' ').trim() ?? '';
    const separator =
      index && words[index - 1].dataset.lineIndex !== word.dataset.lineIndex ? '\n' : ' ';
    return text + (text ? separator : '') + value;
  }, '');
}
